import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import loginBg from '@/assets/background.jpeg';
import appLogo from '@/assets/logo.png';
import { getObjectFileUrl } from '@/components';
import { type AppearanceInfo, getAppearance } from '@/services/appearance';

const DEFAULT_FAVICON_URL = '/favicon.ico';
const DEFAULT_MENU_SELECTED_BG = '#E8EFFD';
const DEFAULT_TOP_NAV_SELECTED_BG = '#3779fa';
const MENU_SELECTED_BG_TINT_RATIO = 0.9;
const TOP_NAV_SELECTED_BG_CHANNEL_OFFSET: [number, number, number] = [
  26, 23, 17,
];
export type RuntimeLayoutSettings = Partial<LayoutSettings> & {
  token?: {
    sider?: Record<string, unknown>;
    header?: Record<string, unknown>;
  };
};

export type AppearanceSource = 'api' | 'backend';
/**
 * 外观来源开关（调试时直接改这里）：
 * - 'api'：通过 branding/current 接口获取
 * - 'backend'：读取当前项目配置（defaultSettings），不请求接口
 */
export const FIXED_APPEARANCE_SOURCE: AppearanceSource = 'api';

/**
 * 动态设置 favicon
 */
export function setFavicon(href: string): void {
  if (typeof document === 'undefined') return;

  // 查找现有的 favicon link 标签
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

  if (!link) {
    // 如果不存在，创建新的 link 标签
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  // 更新 href
  link.href = href;
}

/**
 * 外观资源 URL 解析：
 * 只使用 branding/current 返回的资源 URL
 */
export function getAppearanceAssetUrl(
  assetUrl?: string,
  defaultUrl = '',
): string {
  const url = (assetUrl || '').trim();
  const fallback = (defaultUrl || '').trim();
  return getObjectFileUrl(url) || fallback;
}

export function getAppearanceFaviconUrl(appearance?: AppearanceInfo): string {
  return getAppearanceAssetUrl(
    appearance?.systemFaviconFile?.url,
    DEFAULT_FAVICON_URL,
  );
}

export function getAppearanceMainLogoUrl(appearance?: AppearanceInfo): string {
  return getAppearanceAssetUrl(appearance?.systemLogoFile?.url, appLogo);
}

export function getAppearanceLoginLogoUrl(appearance?: AppearanceInfo): string {
  return getAppearanceAssetUrl(appearance?.loginPageLogoFile?.url, appLogo);
}

export function getAppearanceLoginBgUrl(appearance?: AppearanceInfo): string {
  return getAppearanceAssetUrl(
    appearance?.loginPageBackgroundFile?.url,
    loginBg,
  );
}

function hexToRgb(color: string): [number, number, number] | null {
  const raw = color.trim();
  const rgbMatch = raw.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i,
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    if ([r, g, b].every((v) => Number.isInteger(v) && v >= 0 && v <= 255)) {
      return [r, g, b];
    }
    return null;
  }

  const normalized = raw.replace('#', '').trim();
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clampChannel(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function getMenuSelectedBgColor(primaryColor: string): string {
  const targetPrimaryRgb = hexToRgb(primaryColor);
  if (!targetPrimaryRgb) {
    return DEFAULT_MENU_SELECTED_BG;
  }
  const next = targetPrimaryRgb.map((c) =>
    clampChannel(c + (255 - c) * MENU_SELECTED_BG_TINT_RATIO),
  ) as [number, number, number];
  return rgbToHex(next);
}

export function getTopNavSelectedBgColor(primaryColor: string): string {
  const targetPrimaryRgb = hexToRgb(primaryColor);
  if (!targetPrimaryRgb) {
    return DEFAULT_TOP_NAV_SELECTED_BG;
  }
  const next = targetPrimaryRgb.map((channel, index) =>
    clampChannel(channel + TOP_NAV_SELECTED_BG_CHANNEL_OFFSET[index]),
  ) as [number, number, number];
  return rgbToHex(next);
}

export function setTopNavSelectedBgColor(primaryColor: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(
    '--nebulith-top-nav-selected-bg',
    getTopNavSelectedBgColor(primaryColor),
  );
}

export function buildRuntimeSettings(
  mergedSettings: RuntimeLayoutSettings,
  systemName: string,
  themeColor: string,
): RuntimeLayoutSettings {
  const menuSelectedBg = getMenuSelectedBgColor(themeColor);
  return {
    ...mergedSettings,
    title: systemName,
    colorPrimary: themeColor,
    token: {
      ...mergedSettings.token,
      sider: {
        ...mergedSettings.token?.sider,
        colorTextMenuSelected: themeColor,
        colorTextMenuActive: themeColor,
        colorBgMenuItemSelected: menuSelectedBg,
        colorBgMenuItemHover: menuSelectedBg,
      },
      header: {
        ...mergedSettings.token?.header,
        colorBgHeader: themeColor,
      },
    },
  };
}

export const DEFAULT_BRAND_IDENTITY = '神石';

function getAppearanceFromProjectConfig(
  defaultSettings: RuntimeLayoutSettings,
): AppearanceInfo {
  const title = String(defaultSettings.title || '');
  return {
    brandIdentifier: DEFAULT_BRAND_IDENTITY,
    consoleTitle: title,
    primaryColor: String(defaultSettings.colorPrimary || ''),
  };
}

export function resolveAppearanceBrandIdentity(
  appearance: AppearanceInfo | undefined,
  fallback = DEFAULT_BRAND_IDENTITY,
): string {
  return (appearance?.brandIdentifier || '').trim() || fallback;
}

export function resolveAppearanceSystemName(
  appearance: AppearanceInfo | undefined,
  fallback = '',
): string {
  return appearance?.consoleTitle || fallback;
}

export async function resolveAppearanceRuntime(params: {
  defaultSettings: RuntimeLayoutSettings;
  source?: AppearanceSource;
}): Promise<{
  appearance?: AppearanceInfo;
  appearanceSource: AppearanceSource;
  runtimeSettings: RuntimeLayoutSettings;
  faviconPath?: string;
  themeColor: string;
  systemName: string;
}> {
  const source = params.source || FIXED_APPEARANCE_SOURCE;
  let appearance: AppearanceInfo | undefined;

  if (source === 'backend') {
    appearance = getAppearanceFromProjectConfig(params.defaultSettings);
  } else {
    try {
      appearance = await getAppearance({ skipErrorHandler: true });
    } catch (_error) {
      appearance = undefined;
    }
  }

  const faviconPath = appearance?.systemFaviconFileId;
  const themeColor =
    appearance?.primaryColor || (params.defaultSettings.colorPrimary as string);
  setTopNavSelectedBgColor(themeColor);
  const systemName = resolveAppearanceSystemName(
    appearance,
    String(params.defaultSettings.title || ''),
  );
  const runtimeSettings = buildRuntimeSettings(
    params.defaultSettings,
    systemName,
    themeColor,
  );

  return {
    appearance,
    appearanceSource: source,
    runtimeSettings,
    faviconPath,
    themeColor,
    systemName,
  };
}
